import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Text used as infrastructure HTML parsing utility
import { Text, type TextStyle } from 'react-native';
import { isAndroid } from '../isAndroid';
import { parseHTMLToTextNodes } from './parseHTMLToTextNodes';

/**
 * HTML 문자열을 React Native Text 컴포넌트로 변환
 * @example
 * // 입력
 * renderHTMLToText("<span style='color: #777777;font-size: 16px;'> <b>'≈≈≈'</b>에 대한 검색 결과가 없습니다.</span>")
 *
 * // 결과 (React Native 컴포넌트)
 * <Text>
 *   <Text style={{ color: '#777777', fontSize: 16 }}> </Text>
 *   <Text style={{ color: '#777777', fontSize: 16, fontWeight: 'bold' }}>'≈≈≈'</Text>
 *   <Text style={{ color: '#777777', fontSize: 16 }}>에 대한 검색 결과가 없습니다.</Text>
 * </Text>
 */
export const renderHTMLToText = (
  html: string,
  options?: { style?: TextStyle; numberOfLines?: number },
): React.ReactElement => {
  const nodes = parseHTMLToTextNodes(html);

  const rootStyle: TextStyle | undefined = options?.style;

  if (nodes.length === 0) {
    return (
      <Text style={rootStyle} numberOfLines={options?.numberOfLines}>
        {html}
      </Text>
    );
  }

  return (
    <Text style={rootStyle} numberOfLines={options?.numberOfLines}>
      {nodes.map((node, index) => {
        const nodeStyle: TextStyle = {};

        if (node.color) {
          nodeStyle.color = node.color;
        }

        const fontWeight = isAndroid
          ? {
              regular: 'pretendard_regular',
              bold: 'pretendard_semi_bold',
            }
          : {
              regular: 'Pretendard-Regular',
              bold: 'Pretendard-SemiBold',
            };

        // bold는 명시적으로 설정, normal은 부모 Typography에서 상속
        if (node.fontWeight === 'bold') {
          nodeStyle.fontFamily = fontWeight.bold;
        }

        if (node.fontSize) {
          nodeStyle.fontSize = node.fontSize;
        }

        return (
          <Text key={index} style={nodeStyle}>
            {node.text}
          </Text>
        );
      })}
    </Text>
  );
};
